import type { UseQueryResult } from "@tanstack/react-query";
import type { ColumnDef } from "@tanstack/react-table";

import { DatePicker, ErrorContainer, FormInput, Table } from "@sentricmusic/ui";
import { useNavigate } from "@tanstack/react-router";
import { format } from "date-fns";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { FaRegEdit } from "react-icons/fa";

import type {
  Setlist,
} from "@/api/queries";

import LoadingSmall from "@/components/LoadingSmall";
import useGetColours from "@/helpers/getColours";

type EditCellProps = {
  highlightColour: string;
};

function EditCell({ highlightColour }: EditCellProps) {
  return <FaRegEdit style={{ color: highlightColour }} className="text-2xl gig-claim-editable-pen-icon" />;
}

const createEditCell = (highlightColour: string) => () => <EditCell highlightColour={highlightColour} />;

type AllSetlistsProps = {
  setlists: UseQueryResult<Setlist[], Error>;
};

function AllSetlistsTable({ setlists }: AllSetlistsProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { highlightColour } = useGetColours();

  const [name, setName] = useState("");
  const [date, setDate] = useState<Date | undefined>(undefined);

  const handleClear = () => {
    setName("");
    setDate(undefined);
  };

  const columns = useMemo((): ColumnDef<Setlist>[] => [
    {
      header: String(t("gigs:table-header-name", "Name")),
      accessorKey: "name",
      size: 120,
      id: "colSetlistName",
    },
    {
      header: String(t("dashboard:ra-column-date", "Date")),
      accessorKey: "created",
      size: 120,
      id: "colSetlistDate",
      cell: ({ row }) => {
        const date = row.original.created as string | null | undefined;
        return (date != null && date !== "" && date !== "0000-00-00")
          ? date.split("T")[0]
          : String(t("gigs:unknown-date", "Unknown date"));
      },
    },
    {
      header: String(t("gigs:table-header-songs", "Songs")),
      accessorKey: "songs",
      size: 120,
      id: "colSetlistSongsCount",
      cell: ({ row }) => {
        try {
          return (JSON.parse(row.original.songs ?? "[]") as unknown[]).length;
        }
        catch {
          return 0;
        }
      },
    },
    {
      id: "colSetlistEdit",
      enableSorting: false,
      size: 10,
      cell: createEditCell(highlightColour),
    },
  ], [t, highlightColour]);

  const onRowClick = (row: Setlist) => {
    if (row.id)
      void navigate({ to: `/gigs/setlists/update/${row.id}` });
  };

  const filteredSetlists = useMemo(() => {
    const setlistsData = setlists.data ?? [];

    const nameTerm = name.trim().toLowerCase();
    const dateStr = date ? format(date, "yyyy-MM-dd") : undefined;

    return setlistsData.filter((setlist) => {
      if (nameTerm && !setlist.name?.toLowerCase().includes(nameTerm)) {
        return false;
      }

      if (dateStr !== undefined) {
        const created = setlist.created as string | null | undefined;
        const createdDate = (created != null && created !== "" && created !== "0000-00-00")
          ? created.split("T")[0]
          : null;
        if (createdDate !== dateStr) {
          return false;
        }
      }

      return true;
    });
  }, [setlists.data, name, date]);

  const filterContent = (

    <div className="col-span-4 gap-4 flex flex-col sm:flex-row">
      <FormInput
        label={t("gigs:filter-by-name", "Name")}
        smallFont
        placeholder={`${t("common:filter", "Filter")} ${t("common:setlists", "setlists")}`}
        value={name}
        onChange={e => setName(e.target.value)}
      />

      <DatePicker
        label={t("gigs:filter-by-date", "Date")}
        smallFont
        value={date}
        onChange={setDate}
        placeholder={String(t("gigs:filter-date-placeholder", "Pick a date"))}
      />
    </div>

  );

  const totalSetlists = setlists.data?.length ?? 0;
  const filteredCount = filteredSetlists.length;

  const statsBlock = useMemo(() => [
    { title: t("gigs:stat-total-setlists", "Total setlists"), value: totalSetlists, highlight: false },
    { title: t("gigs:stat-filtered-setlists", "Filtered"), value: filteredCount, highlight: true },
  ], [totalSetlists, filteredCount, t]);

  return (
    <div>
      {setlists.isLoading && <LoadingSmall />}
      {setlists.isSuccess && (
        <div className="overflow-x-auto">
          <Table
            data={filteredSetlists}
            columns={columns}
            onRowClick={onRowClick}
            filterContent={filterContent}
            isRowClickable={(row: Setlist) => !!row.id}
            onClear={handleClear}
            statsBlock={statsBlock}
          />
        </div>
      )}
      {setlists.isError && (
        <ErrorContainer
          message={setlists.error?.message ?? t("common:errorGettingRecentSetlists", "Error getting recent setlists")}
        />
      )}
    </div>
  );
}

export default AllSetlistsTable;
